import type { ChangeEventHandler, FC, HTMLProps } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';

import Icons from '../../Elements/Icons/index.ts';
import Label from '../Label/index.tsx';
import { stopPropagation } from './helpers.ts';
import { IndeterminateIcon } from './IndeterminateIcon.tsx';
import { CheckboxContainer, CheckboxWrapper, HiddenCheckbox, StyledCheckbox } from './styled.ts';

export interface CheckboxProps extends Omit<HTMLProps<HTMLInputElement>, 'ref' | 'as' | 'type' | 'css' | 'onChange'> {
  indeterminate?: boolean;
  label?: string;
  tooltip?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  dataTestId?: string;
}

export const Checkbox: FC<CheckboxProps> = memo(function Checkbox({
  className,
  checked,
  disabled,
  indeterminate,
  label,
  tooltip,
  required,
  onChange,
  dataTestId,
  ...props
}) {
  const { colors } = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const iconColor = useMemo(
    () => (disabled && (checked || indeterminate) ? colors.grey01_2 : colors.grey00_2),
    [disabled, checked, indeterminate, colors.grey01_2, colors.grey00_2],
  );

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.indeterminate = indeterminate ?? false;
  }, [indeterminate]);

  return (
    <CheckboxContainer className={className} disabled={disabled} data-test-checked={checked || false} onClick={stopPropagation}>
      <CheckboxWrapper>
        <HiddenCheckbox ref={inputRef} type='checkbox' checked={checked} disabled={disabled} onChange={onChange} {...props} />
        <StyledCheckbox checked={checked || false} disabled={disabled} $indeterminate={indeterminate} data-testid={dataTestId}>
          {checked && !indeterminate && <Icons.Checkmark size={17} color={iconColor} />}
          {indeterminate && !checked && <IndeterminateIcon color={iconColor} />}
        </StyledCheckbox>
      </CheckboxWrapper>
      {label ? (
        <Label as='span' data-testid={dataTestId} tooltip={tooltip} required={required}>
          {label}
        </Label>
      ) : null}
    </CheckboxContainer>
  );
});

export { HiddenCheckbox, StyledCheckbox };
